import { Box } from "@mui/material";
import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from "react-i18next";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IDateRange } from "../../common/models/IDateRange";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useTankBatteryGraphData } from "../../queries/tank-battery-graph/UseTankBatteryGraphData";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { GraphOptions, ITrendingDisplayOptions } from "../../services/trending-data-service/Models";
import { DataTooltipDisplayEnum } from "../../services/user-settings-data/Models";
import { BaseActionEnum } from "../action-menu/ActionMenuItem";
import Graph, { GraphType } from "../graph/Graph";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import ObjectGraphUtils from "../object-graph/ObjectGraphUtils";

export type TankBatteryLevelGraphProps = {
    dateRange: IDateRange,
    displayOptions: ITrendingDisplayOptions;
};

export type TankBatteryLevelGraphType = {
    executeAction: (action: BaseActionEnum) => void;
    reflow: () => void;
};

function TankBatteryLevelGraph({
    dateRange,
    displayOptions
}: TankBatteryLevelGraphProps, ref: Ref<unknown> | undefined) {

    const { t } = useTranslation();
    const [graphOptions, setGraphOptions] = useState<GraphOptions>();
    const routeContext = useRouteContext();
    const [offlineState] = useOfflineDetectContext();
    const graphRef = useRef<GraphType>(undefined);
    const isPhone = isMobile && !isTablet;

    useImperativeHandle<unknown, TankBatteryLevelGraphType>(
        ref,
        () => ({
            executeAction: executeAction,
            reflow: reflow
        })
    );

    const {
        data: graphData,
        refetch: refetchGraphData,
        status: graphStatus
    } = useQueryNotifications(useTankBatteryGraphData)({
        contextGroupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        dateRange: dateRange as IDateRange,
        displayOptions: displayOptions as ITrendingDisplayOptions,
        windowHeight: window.outerHeight,
        windowWidth: window.outerWidth
    });

    function reflow() {
        graphRef.current?.reflow();
    };

    function executeAction(action: BaseActionEnum) {

        switch (action) {
            case BaseActionEnum.Refresh:
                refetchGraphData();
                break;
        }
    }

    const noDataMessage = graphStatus === 'success' &&
        ((graphData?.trendViewModels?.length ?? 0) === 0 ||
            !graphData?.trendViewModels?.find(o => (o.chartOptions?.series?.length ?? 0) > 0)) ?
        t('objectTagValues.noTankLevelData') :
        undefined;

    useEffect(() => {
        if (graphData && graphData.trendViewModels && (graphData.trendViewModels?.length ?? 0) > 0) {

            const options = ObjectGraphUtils.getGraphOptions(
                routeContext.contextObject,
                graphData,
                DataTooltipDisplayEnum.Separate,
                undefined,
                undefined,
                0
            );

            // chart options returned do not match the types, so cast to any in order to set step
            options?.optionItems?.flatMap(o => o.series).forEach(v => {
                (v as any).step = false;
                (v as any).lineWidth = 2;
            });
            setGraphOptions(options);
        }
    }, [
        graphData,
        setGraphOptions,
        routeContext.contextObject
    ]);

    return (

        <LoadingZone isLoading={graphStatus === "loading"}>
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                flex: '1 1 auto',
                overflow: "hidden"
            }}>

                {!offlineState.offline &&

                    <Box id='mygraph'
                        sx={{
                            padding: noDataMessage ? 2 : 0,
                            display: 'flex',
                            flexDirection: 'column',
                            flex: `1 1 ${isPhone ? '100%' : '50%'}`,
                            '& + &': {
                                marginTop: 1
                            },
                            minHeight: isPhone ? 300 : 200
                        }}
                    >

                        {!noDataMessage && graphOptions &&
                            <Graph ref={graphRef}
                                options={graphOptions}
                                showAxesToggle={false} />
                        }

                        {noDataMessage &&
                            <NoData text={noDataMessage} />
                        }

                    </Box>
                }

            </Box>
        </LoadingZone>
    );
}

export default forwardRef(TankBatteryLevelGraph);